/**
 * =============================================================================
 * SPEQ55 RFP Pro - React Application Entry Point
 * =============================================================================
 *
 * This is the main entry point for the React application. It:
 * 1. Imports global styles (Tailwind CSS)
 * 2. Sets up the React root
 * 3. Wraps the application with necessary providers
 * 4. Renders the main App component
 *
 * Provider Hierarchy:
 * -------------------
 * <React.StrictMode>     - Enables additional development checks
 *   <BrowserRouter>      - Enables client-side routing
 *     <App />            - Main application component
 *
 * Note on StrictMode:
 * -------------------
 * StrictMode is a development tool that:
 * - Highlights potential problems in the application
 * - Detects unsafe lifecycle methods
 * - Warns about deprecated API usage
 * - Detects unexpected side effects
 * It does not render any visible UI and only runs in development.
 *
 * @module main
 * @author SPEQ55
 */

// =============================================================================
// IMPORTS
// =============================================================================

import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

// Main application component
import App from './App';

// Authentication context provider
import { AuthProvider } from './contexts/AuthContext';

// Global styles including Tailwind CSS base, components, and utilities
import './styles/index.css';

// =============================================================================
// APPLICATION BOOTSTRAP
// =============================================================================

/**
 * Get the root DOM element where React will mount.
 * This element is defined in index.html.
 */
const rootElement = document.getElementById('root');

/**
 * Create the React root using the new React 18 createRoot API.
 * This enables concurrent features like automatic batching.
 */
const root = ReactDOM.createRoot(rootElement);

/**
 * Render the application with all necessary providers.
 *
 * Provider Order:
 * 1. StrictMode - React development checks (outermost)
 * 2. BrowserRouter - React Router for navigation
 * 3. AuthProvider - Authentication state management
 * 4. Additional providers can be added here (theme, etc.)
 */
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <App />
      </AuthProvider>
    </BrowserRouter>
  </React.StrictMode>
);

// =============================================================================
// DEVELOPMENT TOOLS
// =============================================================================

/**
 * Log application startup information in development mode.
 * Useful for debugging and confirming the app loaded correctly.
 */
if (import.meta.env.DEV) {
}
